import React from "react";
import { useReducedMotion } from "framer-motion";

/* Motion vocabulary for the work report.

   The rule every value here follows: motion must explain something. A number
   counting up says "this was measured". Bars growing left-to-right say "this is
   a day, and it runs this way". Items sliding in one at a time as the model
   writes says "this is being composed right now, keep watching". Anything that
   moves without saying something got cut.

   Everything degrades: useReducedMotion collapses durations to zero and lands
   every element in its final state, so the page is identical for anyone who has
   asked their OS to stop animating things. */

// Expo-out. Fast start, long settle — reads as "confident", not "bouncy".
export const EASE = [0.16, 1, 0.3, 1];

// For anything that should feel physical rather than timed: bars, expands.
export const SPRING = { type: "spring", stiffness: 380, damping: 32, mass: 0.8 };
export const SPRING_SOFT = { type: "spring", stiffness: 260, damping: 30, mass: 0.9 };

// Entrances are staggered, but the stagger is capped: with twelve items you
// want a wave, not a queue. 34ms each, never more than 300ms total.
export const stagger = (i, step = 0.034, cap = 0.3) => Math.min(i * step, cap);

export function useMotionPrefs() {
  const reduce = useReducedMotion();
  return React.useMemo(
    () => ({
      reduce: !!reduce,
      // Entrance for a card in a list.
      rise: (i = 0) =>
        reduce
          ? { initial: false, animate: { opacity: 1, y: 0 } }
          : {
              initial: { opacity: 0, y: 10 },
              animate: { opacity: 1, y: 0 },
              transition: { delay: stagger(i), duration: 0.42, ease: EASE },
            },
      // Entrance for something that just arrived from the network mid-stream.
      arrive: (i = 0) =>
        reduce
          ? { initial: false, animate: { opacity: 1, y: 0, scale: 1 } }
          : {
              initial: { opacity: 0, y: 14, scale: 0.985 },
              animate: { opacity: 1, y: 0, scale: 1 },
              transition: { delay: stagger(i, 0.05, 0.35), ...SPRING },
            },
      // Growing a bar from nothing to its value.
      grow: (to, i = 0) =>
        reduce
          ? { initial: false, animate: to }
          : {
              initial: Object.keys(to).reduce((a, k) => ({ ...a, [k]: k === "opacity" ? 0 : 0 }), {}),
              animate: to,
              transition: { delay: stagger(i, 0.04, 0.32), duration: 0.6, ease: EASE },
            },
      spring: reduce ? { duration: 0 } : SPRING,
      springSoft: reduce ? { duration: 0 } : SPRING_SOFT,
      dur: (s) => (reduce ? 0 : s),
    }),
    [reduce],
  );
}

/* Count a number up on first paint. Used once, on the hero figure — the single
   number the whole page is about. Overusing this turns a report into a slot
   machine, so it is deliberately not exposed to the stat tiles. */
export function useCountUp(target, { duration = 950, enabled = true } = {}) {
  const reduce = useReducedMotion();
  const live = enabled && !reduce && target > 0;
  const [value, setValue] = React.useState(live ? 0 : target);

  React.useEffect(() => {
    if (!live) { setValue(target); return undefined; }
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      // Expo-out, matching EASE, so the number settles with the cards.
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, live]);

  return value;
}

/* True once the page has scrolled past a sentinel — used to raise the sticky
   control bar only when content is actually underneath it. A bar that is
   shadowed at rest looks broken; one that never shadows looks detached. */
export function useStuck() {
  const ref = React.useRef(null);
  const [stuck, setStuck] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting), { threshold: 1 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return { ref, stuck };
}

/* Hover lift, applied by hand rather than via whileHover so it can be disabled
   wholesale under reduced motion and so touch devices never get a stuck state. */
export function useLift(t, reduce) {
  return React.useMemo(() => {
    if (reduce) return {};
    return {
      onMouseEnter: (e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = t.shadow.md || t.shadow.sm;
        e.currentTarget.style.borderColor = t.c.borderStrong;
      },
      onMouseLeave: (e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = t.shadow.sm;
        e.currentTarget.style.borderColor = t.c.border;
      },
    };
  }, [t, reduce]);
}
